/**
 * Versioned Zod schemas and inferred TypeScript types for all PersonalFinance
 * entities. This file is the source of truth for the data contract.
 *
 * SCHEMA_VERSION follows semver:
 *   - Same major → viewer tolerates (forward-compatible additions)
 *   - Major bump → viewer refuses with a clear message
 */

import { z } from 'zod';
import { zCents } from './money.js';
import { CATEGORIES } from './taxonomy.js';

export const SCHEMA_VERSION = '1.0.0' as const;

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

const zISO8601 = z.string().datetime({ offset: true });

/** Set of all valid category ids derived from the committed taxonomy data. */
const VALID_CATEGORY_IDS = new Set(CATEGORIES.map((c) => c.id));

const zCategoryId = z
  .string()
  .refine((id) => VALID_CATEGORY_IDS.has(id), {
    message: `Category id must be one of the known taxonomy ids`,
  });

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export const AccountSchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    institution: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['cheque', 'savings', 'credit', 'loan', 'offset', 'other']),
    currency: z.string().default('AUD'),
    openingBalanceCents: zCents,
    closingBalanceCents: zCents,
    periodStart: z.string().date(),
    periodEnd: z.string().date(),
  })
  .strict();

export type Account = z.infer<typeof AccountSchema>;

// ---------------------------------------------------------------------------
// Transaction
// ---------------------------------------------------------------------------

export const TransactionSchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    accountId: z.string().min(1),
    postedDate: z.string().date(),
    /** Signed integer cents: positive = credit, negative = debit. */
    amountCents: zCents,
    rawDescription: z.string(),
    normalisedMerchant: z.string(),
    categoryId: zCategoryId,
    isTransfer: z.boolean(),
    transferPairId: z.string().nullable().optional(),
    source: z.enum(['ofx', 'csv', 'manual']),
    dedupeKey: z.string().min(1),
    /**
     * Categorisation confidence score 0–100 (integer).
     * Absent for manual entries or when no categorisation was attempted.
     * M3: changed from 0–1 float to 0–100 integer for clarity and
     * to satisfy the money-as-cents ESLint lint rule (no floats in source).
     */
    confidence: z.number().int().min(0).max(100).optional(),
  })
  .strict();

export type Transaction = z.infer<typeof TransactionSchema>;

// ---------------------------------------------------------------------------
// Category (re-export of taxonomy record as a Zod-validated type)
// ---------------------------------------------------------------------------

export const CategorySchema = z
  .object({
    id: zCategoryId,
    groupId: z.string().min(1),
    name: z.string().min(1),
    isSpend: z.boolean(),
    isNeedsReviewDefault: z.literal(true).optional(),
  })
  .strict();

export type Category = z.infer<typeof CategorySchema>;

// ---------------------------------------------------------------------------
// Loan
// ---------------------------------------------------------------------------

export const LoanSchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    institution: z.string().min(1),
    name: z.string().min(1),
    currentBalanceCents: zCents,
    totalPaidCents: zCents,
    offsetBalanceCents: zCents,
    redrawAvailableCents: zCents,
    asOfDate: z.string().date(),
  })
  .strict();

export type Loan = z.infer<typeof LoanSchema>;

// ---------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------

export const PeriodSchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    accountId: z.string().min(1),
    start: z.string().date(),
    end: z.string().date(),
    openingBalanceCents: zCents,
    closingBalanceCents: zCents,
    reconciled: z.boolean(),
    reconciliationDelta: zCents,
  })
  .strict();

export type Period = z.infer<typeof PeriodSchema>;

// ---------------------------------------------------------------------------
// Insight (advisor blurb — lives inside the envelope but outside contentHash)
// ---------------------------------------------------------------------------

export const InsightSchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    kind: z.enum(['historical', 'monthly']),
    generatedAt: zISO8601,
    model: z.string().min(1),
    aggregatesHash: z.string().min(1),
    text: z.string().min(1),
  })
  .strict();

export type Insight = z.infer<typeof InsightSchema>;

// ---------------------------------------------------------------------------
// AuditEntry
// ---------------------------------------------------------------------------

export const EgressEventSchema = z
  .object({
    kind: z.enum(['categorise', 'advise']),
    payloadShape: z.string().min(1),
    requestId: z.string().optional(),
  })
  .strict();

export const AuditEntrySchema = z
  .object({
    id: z.string().min(1),
    userId: z.string().min(1),
    ts: zISO8601,
    action: z.string().min(1),
    files: z.array(z.string()),
    counts: z.record(z.string(), z.number().int()),
    reconciliation: z.enum(['pass', 'fail', 'partial']),
    egressEvents: z.array(EgressEventSchema),
    approver: z.string().min(1),
    artifactHash: z.string().min(1),
  })
  .strict();

export type AuditEntry = z.infer<typeof AuditEntrySchema>;
export type EgressEvent = z.infer<typeof EgressEventSchema>;

// ---------------------------------------------------------------------------
// ArtifactBody — the deterministic encrypted payload
// ---------------------------------------------------------------------------

/**
 * The body that is serialised to canonical JSON, hashed, then encrypted.
 *
 * All arrays are sorted by id before serialisation so the contentHash is
 * stable across runs with identical data regardless of insertion order.
 * Object keys are sorted alphabetically by canonicalJSON() in artifact.ts.
 */
export const ArtifactBodySchema = z
  .object({
    schemaVersion: z.string(),
    accounts: z.array(AccountSchema),
    transactions: z.array(TransactionSchema),
    /** Category ids only — the full taxonomy is always available at runtime. */
    categoryIds: z.array(zCategoryId),
    loans: z.array(LoanSchema),
    periods: z.array(PeriodSchema),
  })
  .strict();

export type ArtifactBody = z.infer<typeof ArtifactBodySchema>;

/**
 * Strictly validates an artifact body and refuses unknown fields.
 * All nested schemas are .strict() to catch envelope contamination.
 */
export function parseArtifactBody(data: unknown): ArtifactBody {
  return ArtifactBodySchema.parse(data);
}
